import { Search, Menu, X, LogIn, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();

  const handleAuthAction = () => {
    if (isAuthenticated) {
      signOut();
    } else {
      navigate('/auth');
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
      <nav className="container flex h-14 sm:h-16 items-center justify-between px-4 sm:px-6">
        {/* Logo */}
        <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0">
          <a href="/" className="flex items-center space-x-1.5 sm:space-x-2 hover:opacity-80 transition-opacity">
            <img 
              src="/soundfield-logo-small.webp" 
              alt="Soundfield Logo" 
              className="h-6 w-6 sm:h-8 sm:w-8 rounded-lg flex-shrink-0"
              width="32"
              height="32"
            />
            <span className="text-lg sm:text-xl font-bold truncate">Soundfield</span>
          </a>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-6">
          <a href="/" className="text-sm font-medium hover:text-primary transition-colors">Latest</a>
          <a href="/?category=New Music" className="text-sm font-medium hover:text-primary transition-colors">New Music</a>
          <a href="/?category=Industry News" className="text-sm font-medium hover:text-primary transition-colors">Industry News</a>
          <a href="/?category=New Artist Spotlight" className="text-sm font-medium hover:text-primary transition-colors">New Artist Spotlight</a>
          <a href="/?category=Opinion" className="text-sm font-medium hover:text-primary transition-colors">Opinion</a>
        </div>

        {/* Search and Mobile Menu */}
        <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
          <div className="hidden sm:flex items-center space-x-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search articles..."
                className="pl-10 w-48 lg:w-64"
              />
            </div>
          </div>
          
          {/* Admin Link */}
          {isAdmin && (
            <a 
              href="/admin" 
              className="hidden md:block text-xs text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded border border-border hover:bg-accent"
            >
              Admin
            </a>
          )}
          
          {/* Auth Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleAuthAction}
            className="hidden md:flex items-center gap-2"
          >
            {isAuthenticated ? (
              <>
                <LogOut className="h-4 w-4" />
                Sign Out
              </>
            ) : (
              <>
                <LogIn className="h-4 w-4" />
                Sign In
              </>
            )}
          </Button>
          
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden h-8 w-8 p-1"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          >
            {isMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>
        </div>
      </nav>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden border-b border-border bg-background">
          <div className="container py-4 space-y-2">
            <a href="/" className="block py-2 text-sm font-medium hover:text-primary transition-colors">Latest</a>
            <a href="/?category=New Music" className="block py-2 text-sm font-medium hover:text-primary transition-colors">New Music</a>
            <a href="/?category=Industry News" className="block py-2 text-sm font-medium hover:text-primary transition-colors">Industry News</a>
            <a href="/?category=New Artist Spotlight" className="block py-2 text-sm font-medium hover:text-primary transition-colors">New Artist Spotlight</a>
            <a href="/?category=Opinion" className="block py-2 text-sm font-medium hover:text-primary transition-colors">Opinion</a>
            
            {/* Mobile Auth and Admin */}
            <div className="pt-4 border-t border-border space-y-2">
              {isAdmin && (
                <a 
                  href="/admin" 
                  className="block py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  Admin Panel
                </a>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAuthAction}
                className="w-full justify-start"
              >
                {isAuthenticated ? (
                  <>
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </>
                ) : (
                  <>
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign In
                  </>
                )}
              </Button>
            </div>
            
            <div className="pt-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search articles..."
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;